import { storage } from './storage.js';
import { buildCapabilities } from './capabilities.js';
import { loadTheme } from './theme.js';
import { getOrCreateDeviceId } from './deviceId.js';
import { showToast } from './toast.js';
import type { ShellBridge, ModuleRecord, SyncStatus } from './types.js';
import { syncManager } from '../sync/syncManager.js';
import { syncState } from '../sync/syncState.js';
import { getSharedBindings, bindSharedModule, unbindSharedModule } from '../sync/sharedSync.js';
import { refreshAutoSyncCadence } from '../sync/autoSync.js';
import type { SyncProvider } from '../sync/syncProvider.js';
import {
  openRoom, sendToRoom as roomSend, setRoomListener as roomSetListener,
  leaveRoom as roomLeave, roomStateJson,
} from '../sync/gameRoom.js';
import { callAI } from '../ai/aiService.js';
import { getAiProfiles, getAiProfile, setAiProfile, deleteAiProfile } from '../ai/aiProfiles.js';
import {
  scheduleNotification as schedNotification,
  scheduleReminder as schedReminder,
  cancelReminder as cancelReminderImpl,
} from './notifications.js';
import {
  openFilePickerPromise, saveFileWithPicker as saveFileWithPickerImpl,
  exportModuleData as exportModuleDataImpl,
  importModuleData as importModuleDataImpl,
} from './files.js';
import {
  refreshMediaCache,
  hasCameraPermission as hasCameraPermissionImpl,
  hasMicrophonePermission as hasMicrophonePermissionImpl,
  requestCameraPermission as requestCameraPermissionImpl,
  requestMicrophonePermission as requestMicrophonePermissionImpl,
  getCameraList as getCameraListImpl,
  setFavoriteCameraId as setFavoriteCameraIdImpl,
  getFavoriteCameraId as getFavoriteCameraIdImpl,
} from './media.js';
import {
  startRecording as startRecordingImpl,
  stopRecording as stopRecordingImpl,
  playAudio as playAudioImpl,
  pauseAudio as pauseAudioImpl,
  resumeAudio as resumeAudioImpl,
  stopAudio as stopAudioImpl,
  getAudioFileUrl as getAudioFileUrlImpl,
} from './audio.js';

/** Minimal shape of the (non-standard) Battery Status API manager. */
interface WebBatteryManager {
  level: number;
  charging: boolean;
  addEventListener(ev: string, cb: () => void): void;
  removeEventListener(ev: string, cb: () => void): void;
}

// ─── Mediated Google API — service registry + per-module × per-service consent ───
// Mirrors AndroidBridge.kt: host and OAuth scope are resolved natively from
// `service`, so a module can never aim a grant at another Google host. On web
// grants live in localStorage under GOOGLE_GRANTS_KEY; on Android they live in
// SharedPreferences ("google_api_grants") — same JSON shape for the list UI.
interface GoogleSvcDef { host: string; pathPrefix: string; scope: string; label: string; access: string; }
const GOOGLE_SERVICES: Record<string, GoogleSvcDef> = {
  gmail: {
    host: 'https://gmail.googleapis.com', pathPrefix: '/gmail/',
    scope: 'https://www.googleapis.com/auth/gmail.modify', label: 'Gmail',
    access: 'read, send, and modify your email',
  },
  calendar: {
    host: 'https://www.googleapis.com', pathPrefix: '/calendar/',
    scope: 'https://www.googleapis.com/auth/calendar', label: 'Google Calendar',
    access: 'read and manage your calendar events',
  },
  tasks: {
    host: 'https://www.googleapis.com', pathPrefix: '/tasks/',
    scope: 'https://www.googleapis.com/auth/tasks', label: 'Google Tasks',
    access: 'read and manage your task lists',
  },
  sheets: {
    host: 'https://sheets.googleapis.com', pathPrefix: '/v4/spreadsheets',
    scope: 'https://www.googleapis.com/auth/spreadsheets', label: 'Google Sheets',
    access: 'read and edit your spreadsheets',
  },
};
const GOOGLE_GRANTS_KEY = 'shell.googleApiGrants';
interface StoredGrant { moduleId: string; service: string; grantedMs: number; }
function readGoogleGrants(): StoredGrant[] {
  try {
    const raw = localStorage.getItem(GOOGLE_GRANTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(g => g && g.moduleId && g.service) : [];
  } catch { return []; }
}
function writeGoogleGrants(grants: StoredGrant[]): void {
  try { localStorage.setItem(GOOGLE_GRANTS_KEY, JSON.stringify(grants)); } catch { /* quota / private mode */ }
}
function hasGoogleGrant(moduleId: string, service: string): boolean {
  return readGoogleGrants().some(g => g.moduleId === moduleId && g.service === service);
}
function addGoogleGrant(moduleId: string, service: string): void {
  const grants = readGoogleGrants().filter(g => !(g.moduleId === moduleId && g.service === service));
  grants.push({ moduleId, service, grantedMs: Date.now() });
  writeGoogleGrants(grants);
}
function deleteGoogleGrant(moduleId: string, service: string): void {
  const grants = readGoogleGrants().filter(g => !(g.moduleId === moduleId && g.service === service));
  writeGoogleGrants(grants);
}
function ensureGoogleGrant(moduleId: string, service: string, svc: GoogleSvcDef): boolean {
  if (hasGoogleGrant(moduleId, service)) return true;
  const ok = typeof window !== 'undefined' && typeof window.confirm === 'function'
    ? window.confirm(
        `Allow ${svc.label} access?\n\n` +
        `The "${moduleId}" module wants to ${svc.access}.\n\n` +
        `Only allow modules you trust. You can revoke this anytime in Settings.`
      )
    : false;
  if (ok) addGoogleGrant(moduleId, service);
  return ok;
}

/**
 * The web implementation of the Shell bridge. One instance per module —
 * the launcher creates one for itself (id = '__launcher__'), and each
 * iframe gets its own instance scoped to that module's id.
 *
 * Method signatures match AndroidBridge.kt exactly. All values are strings.
 * Async work happens behind the scenes — saveData enqueues to IndexedDB
 * but returns synchronously, just like Android's SharedPreferences-backed
 * version.
 */
export class WebShellBridge implements ShellBridge {
  /** Active geolocation watch ID, if startLocationTracking is running. */
  private locationWatchId: number | null = null;
  /** Most recent clipboard text read (web's getClipboardText is best-effort). */
  private clipboardCache = '';
  /** True when this module runs in an invisible warm-up frame (?bg=1). */
  private readonly background: boolean;
  /** True when this module runs inside the desktop shell's module pane (?embed=1). */
  private readonly embedded: boolean;

  constructor(private readonly moduleId: string, opts?: { background?: boolean; embedded?: boolean }) {
    this.background = opts?.background ?? false;
    this.embedded = opts?.embedded ?? false;
    // Best-effort eager read so the first sync call returns something.
    void this.refreshClipboardCache();
    // Prime the camera/mic caches in the background.
    void refreshMediaCache();
  }

  // --- Core ---
  getShellCapabilities(): string {
    return JSON.stringify(buildCapabilities());
  }

  getModuleId(): string {
    return this.moduleId;
  }

  getDeviceId(): string {
    return getOrCreateDeviceId();
  }

  showToast(msg: string): void {
    // If we're in an iframe, ask the host to render the toast on its
    // document — otherwise it appears clipped inside the module frame.
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ __shell: true, type: 'toast', msg }, window.location.origin);
        return;
      } catch {
        // Cross-origin restriction — fall through to local toast.
      }
    }
    showToast(msg);
  }

  // --- Scoped storage ---
  saveData(key: string, value: string): void {
    storage.set({ moduleId: this.moduleId, key }, value);
  }

  loadData(key: string): string {
    return storage.get({ moduleId: this.moduleId, key });
  }

  deleteData(key: string): void {
    storage.delete({ moduleId: this.moduleId, key });
  }

  // --- Inter-module ---
  publish(key: string, value: string): void {
    // publish() writes to a well-known namespace so peek() can read it.
    storage.set({ moduleId: this.moduleId, key: `__published__::${key}` }, value);
  }

  peek(targetModuleId: string, key: string): string {
    return storage.get({ moduleId: targetModuleId, key: `__published__::${key}` });
  }

  // --- Module registry ---
  getModules(): string {
    const list = storage.listModules().map(([id, rec]) => {
      const r = rec as ModuleRecord;
      return {
        id,
        name: r.name,
        // label is an alias for name — kept for promptgenerator compatibility
        label: r.name,
        icon: r.icon ?? '🧩',
        // All web modules are "local" (no system section)
        isLocal: true,
        section: 'User',
        dataSyncMode: r.dataSyncMode,
        codeSyncMode: r.codeSyncMode,
        lastModifiedMs: r.lastModifiedMs,
      };
    });
    return JSON.stringify(list);
  }

  openModule(targetId: string): void {
    // A warm-up frame must never steal navigation from the visible app.
    if (this.background) {
      console.warn('[bridge] openModule ignored — module is in background warm-up');
      return;
    }
    // Inside the desktop shell's module pane: ask the shell to switch
    // modules instead of navigating — the sidebar stays put.
    // openModule('launcher') (the modules' back button) closes the pane.
    if (this.embedded && window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          { __shell: true, type: 'openModule', id: targetId },
          window.location.origin,
        );
        return;
      } catch {
        // Cross-origin surprise — fall through to full navigation.
      }
    }
    const top = window.top ?? window;
    // "launcher" is a real module on Android but the host page itself on
    // web — modules use openModule('launcher') as their back button.
    if (targetId === 'launcher') {
      top.location.href = new URL('./', window.location.href).toString();
      return;
    }
    const url = new URL('module.html', window.location.href);
    url.searchParams.set('id', targetId);
    // If we're in an iframe, navigate the top window so the module
    // becomes the active screen (matches Android's behavior).
    top.location.href = url.toString();
  }

  /**
   * 'wide' when the user flipped this module to full-width in the desktop
   * shell, 'mobile' otherwise (including on phones). Most modules should
   * just use CSS media/container queries — this is the JS escape hatch.
   */
  getViewportMode(): string {
    if (!this.embedded) return 'mobile';
    return localStorage.getItem(`shell.viewport.${this.moduleId}`) === 'wide' ? 'wide' : 'mobile';
  }

  /**
   * The active theme as JSON ({id, name, bg, surface, accent, labelColor,
   * sectionColor}). Modules normally don't need this — the host injects the
   * tokens as CSS variables — but it matches Android's
   * peek('launcher','theme') shape for JS-driven styling.
   */
  getTheme(): string {
    return JSON.stringify(loadTheme());
  }

  // --- Experience level (app-wide skill level) ---

  /** 'beginner' (default — hide power tools) or 'advanced'. */
  getExperienceLevel(): string {
    return localStorage.getItem('shell.experience') === 'advanced' ? 'advanced' : 'beginner';
  }

  setExperienceLevel(level: string): void {
    localStorage.setItem('shell.experience', level === 'advanced' ? 'advanced' : 'beginner');
  }

  // --- Guided Journey (Wave E) ---
  // Parity stubs: state/spec in localStorage, gating computed in JS. The journey
  // runtime is Android-first; web stubs keep capability-aware modules working.

  getJourneyState(): string {
    return localStorage.getItem('shell.journey.state')
      || '{"v":1,"enrolled":false,"branch":"no-llm","accountMode":"real","localLlmConsented":false,"traits":{},"maxLevelReached":0,"currentLesson":1,"events":[],"sandboxAccounts":{}}';
  }

  setJourneyState(json: string): void {
    try { JSON.parse(json); localStorage.setItem('shell.journey.state', json); } catch { /* ignore */ }
  }

  getJourneySpec(): string {
    return localStorage.getItem('shell.journey.spec') || '{}';
  }

  setJourneySpec(json: string): void {
    try { JSON.parse(json); localStorage.setItem('shell.journey.spec', json); } catch { /* ignore */ }
  }

  /** Web has no embedded default — reset clears the stored spec. */
  resetJourneySpec(): string {
    localStorage.removeItem('shell.journey.spec');
    return '{}';
  }

  journeyLockInfo(featureId: string): string {
    const def = { locked: false, minLevel: 0, label: '', style: 'grey' };
    try {
      const state = JSON.parse(this.getJourneyState());
      if (!state.enrolled) return JSON.stringify(def);
      const gate = (JSON.parse(this.getJourneySpec()).gates || [])
        .find((g: { feature?: string }) => g.feature === featureId);
      if (!gate) return JSON.stringify(def);
      const branch = state.branch || 'no-llm';
      if (gate.branch && gate.branch !== '*' && gate.branch !== branch) return JSON.stringify(def);
      const minLevel = gate.minLevel || 0;
      return JSON.stringify({
        locked: minLevel > (state.maxLevelReached || 0),
        minLevel,
        label: gate.lockedLabel || `Unlocks at Level ${minLevel}`,
        style: gate.style || 'grey',
      });
    } catch { return JSON.stringify(def); }
  }

  journeyLocked(featureId: string): boolean {
    try { return JSON.parse(this.journeyLockInfo(featureId)).locked === true; } catch { return false; }
  }

  journeyEvent(type: string, payload: string): void {
    try {
      const state = JSON.parse(this.getJourneyState());
      state.events = state.events || [];
      let data: unknown; try { data = JSON.parse(payload); } catch { data = payload; }
      state.events.push({ type, ts: Date.now(), data });
      while (state.events.length > 100) state.events.shift();
      this.setJourneyState(JSON.stringify(state));
    } catch { /* ignore */ }
  }

  /** Best-effort device info on web (no RAM API — report 0 / unknown). */
  getDeviceInfo(): string {
    const mem = (navigator as { deviceMemory?: number }).deviceMemory;
    return JSON.stringify({
      totalMemMb: mem ? mem * 1024 : 0,
      availMemMb: 0,
      isLowRamDevice: typeof mem === 'number' ? mem <= 2 : false,
      sdkInt: 0,
      model: 'web',
    });
  }

  /** A module's saved storage keys. */
  moduleDataKeys(targetId: string): string {
    try { return JSON.stringify(storage.listKeysForModule(targetId)); }
    catch { return '[]'; }
  }

  // --- External launch ---

  /** Open a URL in another app/tab (maps, tel:, mailto:, …). */
  openExternalUrl(url: string): boolean {
    if (!url) return false;
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    } catch {
      return false;
    }
  }

  // --- Fullscreen (H1) ---

  /** Go immersive — request browser fullscreen. Best-effort; needs a user gesture. */
  enterFullscreen(): boolean {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) { void el.requestFullscreen(); return true; }
      return false;
    } catch {
      return false;
    }
  }

  /** Leave fullscreen. */
  exitFullscreen(): boolean {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        void document.exitFullscreen();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  /** True while the document is in fullscreen. */
  isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  // --- Keep alive (D7-P4) ---

  private wakeLockSentinel: { release(): Promise<void> } | null = null;

  /** Best-effort Screen Wake Lock — keeps the display on while the tab is visible. */
  keepAlive(on: boolean): boolean {
    const nav = navigator as Navigator & { wakeLock?: { request(type: string): Promise<{ release(): Promise<void> }> } };
    if (on) {
      if (this.wakeLockSentinel) return true;
      if (!nav.wakeLock) return false;
      nav.wakeLock.request('screen')
        .then((s) => { this.wakeLockSentinel = s; })
        .catch(() => { /* denied (background tab / power save) — stay off */ });
      return true;
    }
    void this.wakeLockSentinel?.release().catch(() => {});
    this.wakeLockSentinel = null;
    return false;
  }

  isKeepAlive(): boolean {
    return !!this.wakeLockSentinel;
  }

  // --- Battery (H2) ---

  // navigator.getBattery() is async; cache the manager so the sync bridge
  // methods can answer from the latest values (matches Android's sync shape).
  private batteryManager: WebBatteryManager | null = null;
  private batteryListener: (() => void) | null = null;

  private ensureBattery(cb?: () => void): void {
    const nav = navigator as Navigator & { getBattery?: () => Promise<WebBatteryManager> };
    if (this.batteryManager) { cb?.(); return; }
    nav.getBattery?.().then((m) => { this.batteryManager = m; cb?.(); }).catch(() => {});
  }

  /** JSON {level 0-100 | -1 unknown, charging, powerSaveMode:false} — best-effort on web. */
  getBatteryStatus(): string {
    this.ensureBattery();
    const m = this.batteryManager;
    return JSON.stringify({
      level: m ? Math.round(m.level * 100) : -1,
      charging: m ? m.charging : false,
      powerSaveMode: false,   // not exposed to web pages
    });
  }

  /** Streams battery changes to window[callbackName](jsonString). */
  startBatteryUpdates(callbackName: string): void {
    this.stopBatteryUpdates();
    this.ensureBattery(() => {
      const m = this.batteryManager;
      if (!m) return;
      const fire = () => {
        const cb = (window as unknown as Record<string, unknown>)[callbackName];
        if (typeof cb === 'function') (cb as (s: string) => void)(this.getBatteryStatus());
      };
      this.batteryListener = fire;
      m.addEventListener('levelchange', fire);
      m.addEventListener('chargingchange', fire);
      fire();   // initial snapshot, mirroring the Android sticky broadcast
    });
  }

  stopBatteryUpdates(): void {
    const m = this.batteryManager;
    const l = this.batteryListener;
    if (m && l) {
      m.removeEventListener('levelchange', l);
      m.removeEventListener('chargingchange', l);
    }
    this.batteryListener = null;
  }

  // --- SMS (H3) ---

  /** Open the platform's SMS handler with number + body pre-filled (mobile browsers). */
  sendSms(number: string, body: string): boolean {
    if (!number) return false;
    try {
      const uri = `sms:${encodeURIComponent(number)}${body ? `?body=${encodeURIComponent(body)}` : ''}`;
      window.open(uri, '_self');
      return true;
    } catch {
      return false;
    }
  }

  hasSmsPermission(): boolean { return false; }   // direct SMS is Android-only

  requestSmsPermission(callbackName: string): void {
    const cb = (window as unknown as Record<string, unknown>)[callbackName];
    if (typeof cb === 'function') (cb as (granted: boolean) => void)(false);
  }

  /** Direct (silent) SMS does not exist on web — reports a clean error. */
  sendSmsDirect(_number: string, _body: string, callbackName: string): void {
    const cb = (window as unknown as Record<string, unknown>)[callbackName];
    if (typeof cb === 'function') {
      (cb as (s: string) => void)(JSON.stringify({ ok: false, error: 'Direct SMS is not available on web — use sendSms() instead.' }));
    }
  }

  /**
   * Creates an empty module shell. Returns true on success, false when the
   * id is taken or invalid — mirrors AndroidBridge so the Module Builder's
   * `if (!createModule(...))` guard works on both platforms.
   */
  createModule(id: string, name: string): boolean {
    if (!/^[a-z0-9_-]{1,40}$/.test(id) || id.startsWith('__')) return false;
    if (storage.getModule(id)) return false;
    const placeholder = `<!DOCTYPE html><html><body><p>${name}</p></body></html>`;
    storage.putModule(id, {
      id, name,
      htmlSource: placeholder,
      // module.json carries the label/icon through code sync — it's what
      // Android reads to name the tile when it discovers this module.
      files: {
        'index.html': placeholder,
        'module.json': JSON.stringify({ label: name, icon: '📦', section: 'General' }),
      },
      lastModifiedMs: Date.now(),
      lastDataSyncMs: 0,
      lastCodeSyncMs: 0,
      dataSyncMode: 'local_only',
      codeSyncMode: 'local_only',
    } satisfies ModuleRecord);
    return true;
  }

  /**
   * Replaces a module's code from a {"index.html": "...", ...} file map.
   * The outgoing index.html is archived into files["revisions/…"] first, so
   * History keeps working — and because the files map round-trips through
   * code sync verbatim, the revision reaches Android too.
   */
  importModule(id: string, filesJson: string, comment?: string): boolean {
    void comment; // web keeps no per-revision comments yet
    const rec = storage.getModule(id) as ModuleRecord | undefined;
    if (!rec) return false;
    let incoming: Record<string, string>;
    try {
      incoming = JSON.parse(filesJson) as Record<string, string>;
    } catch {
      return false;
    }
    const newHtml = incoming['index.html'];
    if (typeof newHtml !== 'string' || !newHtml.trim()) return false;

    const files: Record<string, string> = { ...rec.files };
    const isPlaceholder = rec.htmlSource.length < 200 && rec.htmlSource.includes('<p>');
    if (rec.htmlSource && rec.htmlSource !== newHtml && !isPlaceholder) {
      files[`revisions/index_${rec.lastModifiedMs || Date.now()}.html`] = rec.htmlSource;
    }
    for (const [path, content] of Object.entries(incoming)) files[path] = content;

    storage.putModule(id, {
      ...rec,
      htmlSource: newHtml,
      files,
      lastModifiedMs: Date.now(),
    });
    return true;
  }

  getModuleHtmlSource(id: string): string {
    const rec = storage.getModule(id) as ModuleRecord | undefined;
    return rec?.htmlSource ?? '';
  }

  getModuleRevisions(id: string): string {
    const rec = storage.getModule(id) as ModuleRecord | undefined;
    if (!rec) return JSON.stringify([]);
    const out: Array<{ name: string; timestamp: number; isActive?: boolean }> =
      [{ name: 'Current', timestamp: rec.lastModifiedMs, isActive: true }];
    // Archived revisions live in the files map (written by importModule here
    // or pulled from Android via code sync), newest first.
    for (const path of Object.keys(rec.files ?? {})) {
      const m = path.match(/^revisions\/(index_(\d+)\.html)$/);
      if (m) out.push({ name: m[1], timestamp: Number(m[2]) });
    }
    out.sort((a, b) => (a.name === 'Current' ? -1 : b.name === 'Current' ? 1 : b.timestamp - a.timestamp));
    return JSON.stringify(out);
  }

  getModuleRevisionSource(id: string, revisionName: string): string {
    if (revisionName === 'Current') return this.getModuleHtmlSource(id);
    const rec = storage.getModule(id) as ModuleRecord | undefined;
    return rec?.files?.[`revisions/${revisionName}`] ?? '';
  }

  // --- Clipboard ---

  /**
   * Returns the most-recent cached clipboard text. The browser's clipboard
   * API is async, so the very first call after page load may return "" —
   * we kick off a refresh in the background. Modules that need a guaranteed
   * fresh read should listen for a paste event or call setClipboardText
   * to seed the cache.
   */
  getClipboardText(): string {
    void this.refreshClipboardCache();
    return this.clipboardCache;
  }

  setClipboardText(text: string): void {
    this.clipboardCache = text;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch((e) =>
        console.warn('[bridge] clipboard write failed', e));
    }
  }

  private async refreshClipboardCache(): Promise<void> {
    if (typeof navigator === 'undefined') return;
    if (!navigator.clipboard?.readText) return;
    try {
      this.clipboardCache = await navigator.clipboard.readText();
    } catch {
      // Permission denied / no focus — leave the cache as is.
    }
  }

  // --- Camera + microphone ---

  hasCameraPermission(): boolean {
    return hasCameraPermissionImpl();
  }

  requestCameraPermission(callbackName: string): void {
    void (async () => {
      const ok = await requestCameraPermissionImpl();
      this.invokeCallbackRaw(callbackName, ok ? 'true' : 'false');
    })();
  }

  hasMicrophonePermission(): boolean {
    return hasMicrophonePermissionImpl();
  }

  requestMicrophonePermission(callbackName: string): void {
    void (async () => {
      const ok = await requestMicrophonePermissionImpl();
      this.invokeCallbackRaw(callbackName, ok ? 'true' : 'false');
    })();
  }

  /**
   * Returns a JSON array of {id, facing, label}. Android returns this
   * synchronously; on web we mirror that with a cached value (the cache
   * is refreshed on every call in the background).
   */
  getCameraList(): string {
    return JSON.stringify(getCameraListImpl());
  }

  setFavoriteCameraId(id: string): void {
    setFavoriteCameraIdImpl(id);
    this.showToast(id ? `Favorite camera set` : 'Favorite camera cleared');
  }

  getFavoriteCameraId(): string {
    return getFavoriteCameraIdImpl();
  }

  // --- Audio recording + playback ---

  startRecording(fileName: string): string {
    return startRecordingImpl(this.moduleId, fileName);
  }

  stopRecording(): string {
    return stopRecordingImpl(this.moduleId);
  }

  playAudio(fileName: string): void {
    playAudioImpl(this.moduleId, fileName);
  }

  pauseAudio(): void {
    pauseAudioImpl(this.moduleId);
  }

  resumeAudio(): void {
    resumeAudioImpl(this.moduleId);
  }

  stopAudio(): void {
    stopAudioImpl(this.moduleId);
  }

  getAudioFileUrl(fileName: string): string {
    return getAudioFileUrlImpl(this.moduleId, fileName);
  }

  // --- Geolocation ---

  hasLocationPermission(): boolean {
    // We can't ask the Permissions API synchronously, so we treat
    // "geolocation API present" as "permission can be requested". Modules
    // should call requestLocationPermission to know for sure.
    return typeof navigator !== 'undefined' && 'geolocation' in navigator;
  }

  requestLocationPermission(callbackName: string): void {
    if (!('geolocation' in navigator)) {
      this.invokeCallbackRaw(callbackName, 'false');
      return;
    }
    // The cleanest cross-browser "request" is to ask for one position —
    // that triggers the prompt and tells us whether it was granted.
    navigator.geolocation.getCurrentPosition(
      () => this.invokeCallbackRaw(callbackName, 'true'),
      () => this.invokeCallbackRaw(callbackName, 'false'),
      { timeout: 30_000, maximumAge: 60_000 },
    );
  }

  getCurrentLocation(callbackName: string): void {
    if (!('geolocation' in navigator)) {
      this.invokeCallback(callbackName, { error: 'geolocation unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => this.invokeCallback(callbackName, {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => this.invokeCallback(callbackName, { error: err.message }),
      { enableHighAccuracy: true, timeout: 30_000 },
    );
  }

  startLocationTracking(callbackName: string, minTimeMs?: number, minDistanceM?: number): void {
    if (!('geolocation' in navigator)) return;
    // Browsers don't support minTime/minDistance natively, so we throttle.
    const throttleMs = Math.max(0, minTimeMs ?? 3000);
    let lastFire = 0;
    let lastLat: number | null = null;
    let lastLng: number | null = null;
    const minDist = Math.max(0, minDistanceM ?? 0);

    this.stopLocationTracking();
    this.locationWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastFire < throttleMs) return;
        if (minDist > 0 && lastLat != null && lastLng != null) {
          if (haversineMeters(lastLat, lastLng, pos.coords.latitude, pos.coords.longitude) < minDist) return;
        }
        lastFire = now;
        lastLat = pos.coords.latitude;
        lastLng = pos.coords.longitude;
        this.invokeCallback(callbackName, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => console.warn('[bridge] watchPosition error', err),
      { enableHighAccuracy: true },
    );
  }

  stopLocationTracking(): void {
    if (this.locationWatchId != null && 'geolocation' in navigator) {
      navigator.geolocation.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
    }
  }

  // --- Notifications ---

  scheduleNotification(seconds: number, title: string, message: string, type?: string): void {
    schedNotification(this.moduleId, seconds, title, message, type ?? 'test');
  }

  scheduleReminder(intervalMinutes: number, title: string, message: string): void {
    schedReminder(this.moduleId, intervalMinutes, title, message);
  }

  cancelReminder(): void {
    cancelReminderImpl(this.moduleId);
  }

  // --- Files ---

  openFilePicker(callbackName: string): void {
    void (async () => {
      const content = await openFilePickerPromise();
      // Match the Android docs contract: deliver as a string via callback.
      this.invokeCallbackRaw(callbackName, content);
    })();
  }

  saveFileWithPicker(fileName: string, mimeType: string, content: string): void {
    void saveFileWithPickerImpl(fileName, mimeType, content);
  }

  exportModuleData(targetId: string): void {
    // Android signature is exportModuleData(targetId) -> writes a file to
    // Downloads. On web we trigger a save-with-picker for parity.
    const json = exportModuleDataImpl(targetId);
    void saveFileWithPickerImpl(`${targetId}_data.json`, 'application/json', json);
  }

  importModuleData(targetId: string, json: string): void {
    importModuleDataImpl(targetId, json);
  }

  // --- Network ---

  fetchUrl(url: string, optionsJson: string, callbackName: string): void {
    void (async () => {
      const result = await this.doFetch(url, optionsJson);
      // Match the Android bridge: callback receives a JSON STRING that
      // the module parses with JSON.parse().
      this.invokeCallbackRaw(callbackName, JSON.stringify(result));
    })();
  }

  private async doFetch(url: string, optionsJson: string): Promise<{
    ok: boolean; status: number; body: string;
    headers: Record<string, string>; error?: string;
  }> {
    let method = 'GET';
    let body: string | undefined;
    let headers: Record<string, string> = {};
    let timeoutMs = 30_000;

    if (optionsJson?.trim()) {
      try {
        const opts = JSON.parse(optionsJson) as {
          method?: string;
          headers?: Record<string, string>;
          body?: string;
          timeoutMs?: number;
        };
        if (opts.method) method = opts.method.toUpperCase();
        if (opts.body) body = opts.body;
        if (opts.headers) headers = opts.headers;
        if (opts.timeoutMs) timeoutMs = opts.timeoutMs;
      } catch {
        // Ignore bad options — fall back to GET.
      }
    }

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, {
        method,
        headers,
        body: body && method !== 'GET' && method !== 'HEAD' ? body : undefined,
        signal: controller.signal,
      });
      clearTimeout(timer);
      const text = await res.text();
      const respHeaders: Record<string, string> = {};
      res.headers.forEach((v, k) => { respHeaders[k.toLowerCase()] = v; });
      return { ok: res.ok, status: res.status, body: text, headers: respHeaders };
    } catch (e) {
      const msg = (e as Error).message || 'fetch failed';
      // Browser CORS errors show as TypeError "Failed to fetch" — make the hint explicit.
      const hint = msg.includes('Failed to fetch') || msg.includes('NetworkError')
        ? `${msg} (likely CORS — target site doesn't allow browser requests)`
        : msg;
      return { ok: false, status: 0, body: '', headers: {}, error: hint };
    }
  }

  // --- AI ---

  promptAI(prompt: string, systemPrompt: string, profileName: string, callbackName: string): void {
    void (async () => {
      const profile = getAiProfile(profileName);
      if (!profile || !profile.key) {
        this.showToast(`AI Profile '${profileName}' has no key.`);
        this.invokeCallbackRaw(callbackName, `ERROR: Profile '${profileName}' not found or has no key.`);
        return;
      }
      const result = await callAI({
        prompt,
        systemPrompt,
        apiKey: profile.key,
        modelId: profile.model,
        provider: profile.provider as 'gemini' | 'anthropic' | 'openai',
      });
      this.invokeCallbackRaw(callbackName, result);
    })();
  }

  getAIProfiles(): string {
    // Return map without keys (same as Android — key is intentionally hidden from modules).
    const profiles = getAiProfiles();
    const sanitized: Record<string, { model: string; provider: string }> = {};
    for (const [name, p] of Object.entries(profiles)) {
      sanitized[name] = { model: p.model, provider: p.provider };
    }
    return JSON.stringify(sanitized);
  }

  setAIProfile(name: string, key: string, model: string, provider = 'gemini'): void {
    setAiProfile(name, key, model, provider);
    this.showToast(`Profile '${name}' saved.`);
  }

  deleteAIProfile(name: string): void {
    deleteAiProfile(name);
    this.showToast(`Profile '${name}' deleted.`);
  }

  // --- Sync ---
  getSyncStatus(): string {
    const provider = syncManager.getActiveProviderName();
    const snap = syncState.snapshot;
    const status: SyncStatus = {
      configured: provider !== null,
      provider,
      account: syncManager.getAccountEmail(),
      state: snap.status,
      lastSyncMs: snap.lastSyncMs,
      lastError: snap.lastError || undefined,
    };
    return JSON.stringify(status);
  }

  getSyncState(): string {
    const snap = syncState.snapshot;
    return JSON.stringify({
      status: snap.status,
      lastSyncMs: snap.lastSyncMs,
      lastError: snap.lastError,
    });
  }

  connectSyncProvider(name: string, callbackName: string): void {
    void (async () => {
      const ok = name === 'google_drive'
        ? await syncManager.selectProvider('google_drive')
        : false;
      const message = ok
        ? `Connected to ${name}`
        : (syncState.snapshot.lastError || `Failed to connect to ${name}`);
      this.invokeCallback(callbackName, { ok, message });
    })();
  }

  disconnectSyncProvider(callbackName: string): void {
    void (async () => {
      await syncManager.disconnect();
      this.invokeCallback(callbackName, { ok: true, message: 'Disconnected' });
    })();
  }

  syncSelfTest(callbackName: string): void {
    void (async () => {
      const result = await syncManager.selfTest();
      this.invokeCallback(callbackName, result);
    })();
  }

  syncNow(callbackName: string): void {
    void (async () => {
      const result = await syncManager.syncNow();
      this.invokeCallback(callbackName, result);
    })();
  }

  getModuleSyncModes(targetId: string): string {
    const rec = storage.getModule(targetId) as ModuleRecord | undefined;
    return JSON.stringify({
      dataMode: rec?.dataSyncMode ?? 'local_only',
      codeMode: rec?.codeSyncMode ?? 'local_only',
    });
  }

  setModuleSyncModes(targetId: string, dataMode: string, codeMode: string): void {
    const rec = storage.getModule(targetId) as ModuleRecord | undefined;
    if (!rec) return;
    const normalize = (m: string): 'local_only' | 'sync' => m === 'sync' ? 'sync' : 'local_only';
    storage.putModule(targetId, {
      ...rec,
      dataSyncMode: normalize(dataMode),
      codeSyncMode: normalize(codeMode),
    });
  }

  // --- Shared module data (F2 — family & friends) ---

  getSharedSyncState(): string {
    const provider = syncManager.get();
    const bindings: Record<string, unknown> = {};
    for (const [id, b] of Object.entries(getSharedBindings())) {
      bindings[id] = {
        folderId: b.folderId, folderName: b.folderName,
        link: b.link, lastMergedMs: b.lastMergedMs,
      };
    }
    return JSON.stringify({
      supported: !!(provider && provider.isAuthenticated() && provider.supportsSharedFolders?.()),
      hasScope: provider?.hasSharedScope?.() ?? false,
      bindings,
    });
  }

  shareModuleData(targetModuleId: string, callbackName: string): void {
    void (async () => {
      await syncManager.initFromStorage();
      const provider = syncManager.get();
      if (!provider || !provider.isAuthenticated()) {
        this.invokeCallback(callbackName, { ok: false, error: 'Connect Google Drive in Settings first' }); return;
      }
      if (!provider.supportsSharedFolders?.()) {
        this.invokeCallback(callbackName, { ok: false, error: "This sync provider can't host shared folders" }); return;
      }
      if (!(await provider.requestSharedScope?.())) {
        this.invokeCallback(callbackName, { ok: false, error: 'Drive permission not granted' }); return;
      }
      const rec = storage.getModule(targetModuleId) as ModuleRecord | undefined;
      const label = rec?.name ?? targetModuleId;
      const info = await provider.createSharedFolder?.(`Shell Shared — ${label}`);
      if (!info) {
        this.invokeCallback(callbackName, { ok: false, error: "Couldn't create the shared folder" }); return;
      }
      bindSharedModule(targetModuleId, {
        folderId: info.id, folderName: info.name, link: info.link,
        knownKeys: [], lastMergedMs: 0,
      });
      refreshAutoSyncCadence();
      try { await syncManager.syncNow(); } catch { /* engine logs */ }
      this.invokeCallback(callbackName, { ok: true, folderId: info.id, folderName: info.name, link: info.link });
    })();
  }

  joinSharedModuleData(targetModuleId: string, folderIdOrLink: string, callbackName: string): void {
    void (async () => {
      await syncManager.initFromStorage();
      const provider = syncManager.get();
      if (!provider || !provider.isAuthenticated()) {
        this.invokeCallback(callbackName, { ok: false, error: 'Connect Google Drive in Settings first' }); return;
      }
      if (!provider.supportsSharedFolders?.()) {
        this.invokeCallback(callbackName, { ok: false, error: "This sync provider can't host shared folders" }); return;
      }
      // The folder only carries the module's DATA, not the module itself —
      // joining without the module installed would merge records the user
      // has no UI for. Ask them to install first.
      if (!storage.getModule(targetModuleId)) {
        this.invokeCallback(callbackName, { ok: false, error: 'Install the module first — the shared folder only holds its data. Get the module from whoever shared it, then join again.' }); return;
      }
      const folderId = extractDriveFolderId(folderIdOrLink);
      if (!folderId) {
        this.invokeCallback(callbackName, { ok: false, error: "That doesn't look like a Drive folder link or ID" }); return;
      }
      if (!(await provider.requestSharedScope?.())) {
        this.invokeCallback(callbackName, { ok: false, error: 'Drive permission not granted' }); return;
      }
      const info = await provider.getSharedFolderInfo?.(folderId);
      if (!info) {
        this.invokeCallback(callbackName, { ok: false, error: 'Folder not found — make sure it was shared with this Google account and you accepted the invite' }); return;
      }
      bindSharedModule(targetModuleId, {
        folderId: info.id, folderName: info.name, link: info.link,
        knownKeys: [], lastMergedMs: 0,
      });
      refreshAutoSyncCadence();
      try { await syncManager.syncNow(); } catch { /* engine logs */ }
      this.invokeCallback(callbackName, { ok: true, folderName: info.name, link: info.link });
    })();
  }

  unshareModuleData(targetModuleId: string): boolean {
    const removed = unbindSharedModule(targetModuleId);
    if (removed) refreshAutoSyncCadence();
    return removed;
  }

  // --- Game rooms (F1-a — turn-based multiplayer over shared-Drive files) ---
  // Transport-agnostic room API (create/join/send/receive/leave); today the
  // transport is Drive-folder polling (~2–5 s one-way). Runs inside the
  // module's iframe, so the poll loop dies with the module.

  /** Same preflight as the F2 share flow. Returns provider or posts an error. */
  private async roomProviderOrError(callbackName: string): Promise<SyncProvider | null> {
    // A module iframe has its own JS context and its own uninitialised
    // syncManager singleton — this awaits the launcher-parity silent
    // resume before deciding "not connected".
    await syncManager.initFromStorage();
    const provider = syncManager.get();
    if (!provider || !provider.isAuthenticated()) {
      this.invokeCallback(callbackName, { ok: false, error: 'Connect Google Drive in Settings first' }); return null;
    }
    if (!provider.supportsSharedFolders?.()) {
      this.invokeCallback(callbackName, { ok: false, error: "This sync provider can't host rooms" }); return null;
    }
    if (!(await provider.requestSharedScope?.())) {
      this.invokeCallback(callbackName, { ok: false, error: 'Drive permission not granted' }); return null;
    }
    return provider;
  }

  createRoom(roomName: string, playerName: string, callbackName: string): void {
    void (async () => {
      const provider = await this.roomProviderOrError(callbackName);
      if (!provider) return;
      const info = await provider.createSharedFolder?.(`Shell Room — ${roomName.trim() || 'Game'}`);
      if (!info) {
        this.invokeCallback(callbackName, { ok: false, error: "Couldn't create the room folder" }); return;
      }
      // Best-effort: without this the creator must share the folder manually
      // from the Drive UI before anyone can join.
      const linkShared = (await provider.makeFolderLinkShared?.(info.id)) ?? false;
      const { playerId } = await openRoom(provider, info, playerName);
      this.invokeCallback(callbackName, {
        ok: true, code: info.id, name: info.name, link: info.link, playerId, linkShared,
      });
    })();
  }

  joinRoom(codeOrLink: string, playerName: string, callbackName: string): void {
    void (async () => {
      const folderId = extractDriveFolderId(codeOrLink);
      if (!folderId) {
        this.invokeCallback(callbackName, { ok: false, error: "That doesn't look like a room link or code" }); return;
      }
      const provider = await this.roomProviderOrError(callbackName);
      if (!provider) return;
      const info = await provider.getSharedFolderInfo?.(folderId);
      if (!info) {
        this.invokeCallback(callbackName, { ok: false, error: "Room not found — check the link, and make sure the creator's link sharing worked" }); return;
      }
      const { playerId } = await openRoom(provider, info, playerName);
      this.invokeCallback(callbackName, {
        ok: true, code: info.id, name: info.name, link: info.link, playerId,
      });
    })();
  }

  sendToRoom(dataJson: string): void {
    roomSend(dataJson);
  }

  onRoomMessage(callbackName: string): void {
    roomSetListener((payload) => this.invokeCallbackRaw(callbackName, payload));
  }

  leaveRoom(): void {
    roomLeave();
  }

  getRoomState(): string {
    return roomStateJson();
  }

  // --- Google OAuth (module-facing) ---

  /**
   * Mediated Google API call. Web parity mirrors the Android bridge shape:
   * Shell resolves the host + scope from `service`, asks the user once per
   * module × service, then attaches the OAuth token itself and returns only
   * the response body. On web the "token stays out of JS" guarantee is weaker
   * than on Android (any script on this origin can see the closure state at
   * runtime); the value here is API parity and the per-module consent gate,
   * not Keystore-grade secrecy.
   */
  googleApi(
    service: string, verb: string, path: string,
    query: string, body: string, callbackName: string
  ): void {
    void (async () => {
      const svc = GOOGLE_SERVICES[service.trim().toLowerCase()];
      if (!svc) {
        this.invokeCallback(callbackName, { ok: false, error: `Unknown Google service '${service}'` });
        return;
      }
      if (!path.startsWith('/') || path.includes('://') || !path.startsWith(svc.pathPrefix)) {
        this.invokeCallback(callbackName, {
          ok: false,
          error: `Path must start with ${svc.pathPrefix} for the ${service} service`,
        });
        return;
      }
      if (!ensureGoogleGrant(this.moduleId, service.trim().toLowerCase(), svc)) {
        this.invokeCallback(callbackName, {
          ok: false, denied: true,
          error: `You declined ${svc.label} access for this module.`,
        });
        return;
      }
      const { getAccessTokenForScopes } = await import('../sync/googleAuth.js');
      const tokenRes = await getAccessTokenForScopes(svc.scope);
      if (!tokenRes.ok || !tokenRes.token) {
        this.invokeCallback(callbackName, { ok: false, error: tokenRes.error || 'Google authorization failed' });
        return;
      }
      const q = query.trim().replace(/^[?&]/, '');
      const sep = path.includes('?') ? '&' : '?';
      const url = svc.host + path + (q ? sep + q : '');
      const headers: Record<string, string> = { Authorization: 'Bearer ' + tokenRes.token };
      const reqBody = body || null;
      if (reqBody) headers['Content-Type'] = 'application/json';
      try {
        const resp = await fetch(url, {
          method: (verb || 'GET').toUpperCase(),
          headers,
          body: reqBody ?? undefined,
        });
        const text = await resp.text();
        const respHeaders: Record<string, string> = {};
        resp.headers.forEach((v, k) => { respHeaders[k.toLowerCase()] = v; });
        this.invokeCallback(callbackName, {
          ok: resp.ok, status: resp.status, body: text, headers: respHeaders,
        });
      } catch (e) {
        this.invokeCallback(callbackName, {
          ok: false, status: 0, body: '', headers: {},
          error: (e as Error).message || 'Network error',
        });
      }
    })();
  }

  /** (Settings-only) JSON array [{moduleId, service, grantedMs}]. */
  listGoogleApiGrants(callbackName: string): void {
    if (this.moduleId !== 'settings') {
      this.invokeCallbackRaw(callbackName, '[]');
      return;
    }
    const grants = readGoogleGrants();
    this.invokeCallbackRaw(callbackName, JSON.stringify(grants));
  }

  /** Revokes a grant. Settings can revoke any; other modules can revoke their own. */
  revokeGoogleApiGrant(targetModuleId: string, service: string): void {
    if (this.moduleId !== 'settings' && targetModuleId !== this.moduleId) return;
    deleteGoogleGrant(targetModuleId, service.trim().toLowerCase());
  }

  // --- Background refresh ---

  setModuleBackgroundRefresh(targetId: string, enabled: boolean): void {
    const rec = storage.getModule(targetId) as ModuleRecord | undefined;
    if (!rec) return;
    storage.putModule(targetId, { ...rec, backgroundRefresh: !!enabled });
  }

  /**
   * Modules use this to skip heavy work (camera, animation) when loaded
   * invisibly — during background warm-up or while the tab is hidden.
   */
  isModuleVisible(): boolean {
    return !this.background && document.visibilityState === 'visible';
  }

  // --- Running modules (Android parity) ---
  // On web, modules are destroyed when the user navigates away (mobile) or
  // switches the pane (desktop), so nothing accumulates in the background —
  // these exist so cross-platform modules can call them safely.

  /** Always [] on web: background modules don't persist here. */
  getRunningModules(): string {
    return '[]';
  }

  /** No-op on web — there is never a background WebView to stop. */
  stopModule(_targetId: string): boolean {
    return false;
  }

  /** Stored for parity; web tears modules down on switch anyway. */
  setModuleStopOnLeave(targetId: string, enabled: boolean): void {
    const rec = storage.getModule(targetId) as ModuleRecord | undefined;
    if (!rec) return;
    storage.putModule(targetId, { ...rec, stopOnLeave: !!enabled });
  }

  // ─── helpers ────────────────────────────────────────────────────────

  /** Calls window[name](JSON.stringify(payload)) — used for object payloads. */
  private invokeCallback(name: string, payload: unknown): void {
    try {
      const fn = (window as unknown as Record<string, unknown>)[name];
      if (typeof fn === 'function') (fn as (s: string) => void)(JSON.stringify(payload));
      else console.warn(`[bridge] callback '${name}' not found on window`);
    } catch (e) {
      console.error(`[bridge] callback '${name}' threw`, e);
    }
  }

  /** Calls window[name](rawString) — used when the string is already formatted (AI result). */
  private invokeCallbackRaw(name: string, raw: string): void {
    try {
      const fn = (window as unknown as Record<string, unknown>)[name];
      if (typeof fn === 'function') (fn as (s: string) => void)(raw);
      else console.warn(`[bridge] callback '${name}' not found on window`);
    } catch (e) {
      console.error(`[bridge] callback '${name}' threw`, e);
    }
  }
}

/** Accepts a bare Drive folder ID or any drive.google.com folder URL. */
function extractDriveFolderId(input: string): string | null {
  const trimmed = input.trim();
  const byPath = /\/folders\/([A-Za-z0-9_-]+)/.exec(trimmed);
  if (byPath) return byPath[1]!;
  const byQuery = /[?&]id=([A-Za-z0-9_-]+)/.exec(trimmed);
  if (byQuery) return byQuery[1]!;
  return /^[A-Za-z0-9_-]{10,}$/.test(trimmed) ? trimmed : null;
}

/** Great-circle distance in meters between two lat/lng pairs. */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // earth radius in meters
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Install the bridge globally under both names — `window.Shell` (official,
 * cross-platform) and `window.Android` (legacy alias, kept on Android only
 * per BRIDGE.md). On web we ONLY install `window.Shell` — modules still on
 * `window.Android` will fail loud, which is the documented behavior.
 */
export function installBridge(bridge: WebShellBridge): void {
  (window as any).Shell = bridge;
  // Intentionally do NOT install window.Android on web. See docs/BRIDGE.md.
}
